import { useTranslation } from "@i18n";
import { useCallback, useState, useRef } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Box, Slider, IconButton, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";

// Helper function to create image from URL
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.crossOrigin = "anonymous";
    image.src = url;
  });

// Helper function to get cropped image as base64
async function getCroppedImg(imageSrc: string, pixelCrop: Area, rotation = 0): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  const rotRad = (rotation * Math.PI) / 180;

  // Calculate bounding box of the rotated image
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate canvas context to center before rotating
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  // Calculate the position of cropped area in rotated image
  const data = ctx.getImageData(0, 0, bBoxWidth, bBoxHeight);

  // Set canvas size to final cropped size
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Paste the cropped area
  ctx.putImageData(
    data,
    Math.round(0 - pixelCrop.x - (bBoxWidth - image.width) / 2),
    Math.round(0 - pixelCrop.y - (bBoxHeight - image.height) / 2),
  );

  return canvas.toDataURL("image/jpeg", 0.9);
}

function ImageEditorModal({
  width = "900px",
  allowModalPadding = false,
  heading,
  closeModal,
  onSubmitFunction,
  existingImg,
}: any) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(existingImg || null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result as string);
      });
      reader.readAsDataURL(file!);
    }
  };

  const handleOpenFile = () => {
    fileInputRef.current?.click();
  };

  const saveImageData = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      await onSubmitFunction(croppedImage);
      closeModal();
    } catch (e) {
      console.error("Error cropping image:", e);
      Promise.reject(e);
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 1));
  const handleRotateLeft = () => setRotation((prev) => prev - 90);
  const handleRotateRight = () => setRotation((prev) => prev + 90);
  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  const headerContent = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <span>{heading || t("Edit Picture")}</span>
    </div>
  );

  const modelContent = () => (
    <Box>
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*" style={{ display: "none" }} />

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          p: "0.5rem",
          borderBottom: "1px solid #e0e0e0",
          flexWrap: "wrap",
        }}
      >
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleOpenFile}>
          {t("Open")}
        </ButtonComponent>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <IconButton size="small" onClick={handleZoomOut} title={t("Zoom Out")}>
            <ZoomOutIcon />
          </IconButton>
          <IconButton size="small" onClick={handleZoomIn} title={t("Zoom In")}>
            <ZoomInIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <IconButton size="small" onClick={handleRotateLeft} title={t("Rotate Left")}>
            <RotateLeftIcon />
          </IconButton>
          <IconButton size="small" onClick={handleRotateRight} title={t("Rotate Right")}>
            <RotateRightIcon />
          </IconButton>
        </Box>
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleReset}>
          {t("Reset")}
        </ButtonComponent>
      </Box>

      {/* Cropper Area */}
      <Box
        className="profileCropperContent"
        sx={{
          position: "relative",
          height: "21.875rem",
          background: "#333",
        }}
      >
        {imageSrc ? (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#999",
            }}
          >
            <Typography>{t("Click 'Open' to select an image")}</Typography>
          </Box>
        )}
      </Box>

      {/* Zoom Slider */}
      {imageSrc && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", p: "1rem" }}>
          <Typography variant="body2" sx={{ minWidth: "3.125rem" }}>
            {t("Zoom")}:
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value as number)}
            sx={{ flex: 1 }}
          />
        </Box>
      )}
    </Box>
  );

  const footerContent = () => (
    <div className="flex gap-4">
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
        {t("Cancel")}
      </ButtonComponent>
      <ButtonComponent disabled={!imageSrc} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={saveImageData}>
        {t("Save Changes")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      width={width}
      allowModalPadding={allowModalPadding}
      headerTitle={headerContent()}
      footerContent={footerContent()}
      onCancel={closeModal}
      modelContent={modelContent()}
      isVisible
    />
  );
}

export default ImageEditorModal;
